/**
 * Time Management Matrix - Main App Component
 * 
 * A Eisenhower Matrix (4-quadrant) task management application with:
 * - Local storage persistence
 * - Dark/Light theme toggle
 * - File save/load capabilities (with Safari fallback)
 * - Drag-and-drop task reordering
 * - Inline task editing
 * - Real-time drop position preview
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';

// Component imports
import { Header } from './components/Header';
import { Quadrant } from './components/Quadrant';
import { SaveToast } from './components/SaveToast';
import { GamificationBar } from './components/GamificationBar';
import { RewardToast } from './components/RewardToast';
import { AnalyticsModal } from './components/AnalyticsModal';
import { DailyFocusView } from './components/DailyFocusView';
import { SettingsModal } from './components/SettingsModal';

// Hooks imports
import {
  useQuadrants,
  useDarkMode,
  useAddTask,
  useToggleTask,
  useRemoveTask,
  useMoveTask,
  useUpdateQuadrant,
  useTogglePlanToday,
} from './hooks/useDataManagement';
import {
  useFileSystemSupport,
  useLoadFromFile,
  useToast,
} from './hooks/useFileOperations';
import { useAuth } from './contexts/AuthContext';
import { useSupabaseSync } from './hooks/useSupabaseSync';
import { useGamification, getLocalDateString } from './hooks/useGamification';
import { useProductivityStats } from './hooks/useProductivityStats';
import { useAppSettings } from './hooks/useAppSettings';

// Styles
import './App.css';

/**
 * Main App Component
 * Orchestrates all state and user interactions
 */
function App() {
  // Data management hooks
  const [quadrants, setQuadrants] = useQuadrants();
  const [isDarkMode, setIsDarkMode] = useDarkMode();

  // File operations
  const { isFileSystemSupported, isLegacyFileInputSupported } = useFileSystemSupport();
  const { filePickerInput, loadDataLegacy, handleFileInputChange } = useLoadFromFile();
  const { showSaveToast, toastMessage, showToast } = useToast();

  // Auth & Sync
  const { user, loginWithGoogle, logout } = useAuth();
  useSupabaseSync(user, quadrants, setQuadrants, showToast);

  // Gamification state
  const {
    gameState,
    levelInfo,
    rankInfo,
    recentReward,
    clearRecentReward,
    earnTaskReward,
    revertTaskReward,
  } = useGamification();

  // Task operation callbacks
  const addTask = useAddTask(setQuadrants);
  const toggleTask = useToggleTask(setQuadrants);
  const removeTask = useRemoveTask(setQuadrants);
  const moveTask = useMoveTask(setQuadrants);
  const updateQuadrant = useUpdateQuadrant(setQuadrants);
  const togglePlanToday = useTogglePlanToday(setQuadrants);

  // Productivity Analytics
  const stats = useProductivityStats(quadrants);

  // Gamified toggle handler
  const handleToggleTask = useCallback(
    (quadrantId, taskId) => {
      const quadrant = quadrants.find((q) => q.id === quadrantId);
      const task = quadrant?.tasks?.find((t) => t.id === taskId);
      if (!task) return;

      const willBeCompleted = !task.completed;
      toggleTask(quadrantId, taskId);

      if (willBeCompleted) {
        earnTaskReward(quadrantId);
      } else {
        revertTaskReward(quadrantId);
      }
    },
    [quadrants, toggleTask, earnTaskReward, revertTaskReward]
  );

  // Total completed today calculation
  const todayStr = getLocalDateString();
  const totalCompletedToday = quadrants.reduce((acc, q) => {
    return (
      acc +
      (q.tasks || []).filter(
        (t) => t.completed && t.completedAt && t.completedAt.startsWith(todayStr)
      ).length
    );
  }, 0);

  // UI State
  const [inputs, setInputs] = useState({ 0: '', 1: '', 2: '', 3: '' });
  const [activeMobileTab, setActiveMobileTab] = useState(0);
  const [viewMode, setViewMode] = useState('matrix'); // 'matrix' | 'daily'
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [fileHandle, setFileHandle] = useState(null);

  // App Settings & Custom Wallpaper
  const {
    settings,
    activeWallpaperUrl,
    updateSetting,
    selectWallpaper,
    setCustomWallpaper,
    resetSettings,
  } = useAppSettings();
  const [openConfigId, setOpenConfigId] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverQuadrantId, setDragOverQuadrantId] = useState(null);
  const [dropPosition, setDropPosition] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [lastAddedQuadrantId, setLastAddedQuadrantId] = useState(null);

  // Refs
  const taskListRefs = useRef({});
  const isPickingFile = useRef(false);

  /**
   * Saves quadrant data to disk using File System API
   * Falls back to download if API unavailable
   */
  const saveToDisk = useCallback(
    async (handle = fileHandle) => {
      if (!handle) return;
      try {
        const writable = await handle.createWritable();
        await writable.write(JSON.stringify(quadrants, null, 2));
        await writable.close();
        showToast('File saved successfully');
      } catch (err) {
        console.error('Save to disk failed', err);
      }
    },
    [quadrants, fileHandle, showToast]
  );

  /**
   * Loads data from a JSON file using File System API
   */
  const loadData = useCallback(async () => {
    if (isPickingFile.current) return;
    isPickingFile.current = true;

    try {
      const [handle] = await window.showOpenFilePicker({
        types: [{ description: 'JSON File', accept: { 'application/json': ['.json'] } }],
      });

      const file = await handle.getFile();
      const text = await file.text();
      const data = JSON.parse(text);

      setQuadrants((prev) => {
        return prev.map((q) => {
          const importedQ = data.find((iq) => iq.id === q.id);
          if (importedQ) {
            const validTasks = (importedQ.tasks || []).map(t => {
              const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(t.id));
              return { ...t, id: isUuid ? t.id : crypto.randomUUID() };
            });
            return { ...importedQ, tasks: validTasks, dbId: q.dbId };
          }
          return q;
        });
      });
      setFileHandle(handle);
      showToast('File loaded successfully');
    } catch (err) {
      console.log('File load cancelled or failed', err);
    } finally {
      isPickingFile.current = false;
    }
  }, [setQuadrants, showToast]);

  /**
   * Saves data to a new file and switches to direct save mode
   */
  const saveData = useCallback(async () => {
    if (isPickingFile.current) return;
    isPickingFile.current = true;

    try {
      if (isFileSystemSupported) {
        const handle = await window.showSaveFilePicker({
          suggestedName: 'time-management-matrix.json',
          types: [{ description: 'JSON File', accept: { 'application/json': ['.json'] } }],
        });
        setFileHandle(handle);
        await saveToDisk(handle);
      } else {
        // Fallback for Safari and unsupported browsers
        const blob = new Blob([JSON.stringify(quadrants, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'time-management-matrix.json';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        showToast('File downloaded successfully');
      }
    } catch (err) {
      console.log('File save cancelled or failed', err);
    } finally {
      isPickingFile.current = false;
    }
  }, [isFileSystemSupported, quadrants, saveToDisk, showToast]);

  /**
   * Auto-saves to localStorage whenever quadrants change
   */
  useEffect(() => {
    localStorage.setItem('tm-matrix-data', JSON.stringify(quadrants));
  }, [quadrants]);

  /**
   * Auto-saves to file every 30 seconds if file is linked
   */
  useEffect(() => {
    const interval = setInterval(() => {
      if (fileHandle) saveToDisk();
    }, 30000);
    return () => clearInterval(interval);
  }, [fileHandle, saveToDisk]);

  /**
   * Handles keyboard shortcuts (Ctrl+S / Cmd+S)
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (fileHandle) {
          saveToDisk();
        } else {
          showToast('Saved to Local Storage');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fileHandle, saveToDisk, showToast]);

  /**
   * Closes config dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openConfigId && !event.target.closest('.quadrant-actions')) {
        setOpenConfigId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openConfigId]);

  /**
   * Auto-scrolls task list when a new task is added
   */
  useEffect(() => {
    if (lastAddedQuadrantId === null) return;
    const node = taskListRefs.current[lastAddedQuadrantId];
    if (node && node.scrollTo) {
      node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
    }
    setLastAddedQuadrantId(null);
  }, [quadrants, lastAddedQuadrantId]);

  /**
   * Saves edited task text
   */
  const saveTaskEdit = useCallback(
    (quadrantId, taskId) => {
      const newText = inputs[`edit-task-${taskId}`]?.trim();
      if (newText) {
        setQuadrants((prev) =>
          prev.map((q) =>
            q.id === quadrantId
              ? {
                ...q,
                tasks: q.tasks.map((t) =>
                  t.id === taskId ? { ...t, text: newText } : t
                ),
              }
              : q
          )
        );
      }
      setInputs((prev) => ({ ...prev, [`edit-task-${taskId}`]: undefined }));
      setEditingTask(null);
    },
    [inputs, setQuadrants]
  );

  /**
   * Helper to compute the visual index using exact DOM elements
   */
  const getVisualDropIndex = useCallback((e, quadrantId) => {
    const container = document.querySelector(`[data-quadrant-id="${quadrantId}"] .task-list`);
    if (!container) return 0;

    const items = Array.from(container.querySelectorAll('.task-item:not(.completed)'));
    const y = e.clientY;

    let index = items.length;
    for (let i = 0; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();
      const middleY = rect.top + rect.height / 2;
      if (y < middleY) {
        index = i;
        break;
      }
    }
    return index;
  }, []);

  /**
   * Helper to map visual active index to the absolute array index
   */
  const getAbsoluteDropIndex = useCallback((quadrantId, visualIndex) => {
    const quadrant = quadrants.find((q) => q.id === quadrantId);
    if (!quadrant) return 0;
    const tasks = quadrant.tasks || [];
    const activeTasks = tasks.filter((t) => !t.completed);

    if (visualIndex < activeTasks.length) {
      return tasks.indexOf(activeTasks[visualIndex]);
    } else {
      if (activeTasks.length === 0) return 0; // Or tasks.length to append at the very end
      return tasks.indexOf(activeTasks[activeTasks.length - 1]) + 1;
    }
  }, [quadrants]);

  /**
   * Handles drag over event for drop position preview
   */
  const handleDragOver = useCallback((e, quadrantId) => {
    e.preventDefault();
    const visualIndex = getVisualDropIndex(e, quadrantId);

    setDropPosition({
      quadrantId,
      index: visualIndex,
    });
  }, [getVisualDropIndex]);

  /**
   * Handles drop event for task movement
   */
  const handleDrop = useCallback(
    (e, toQuadrantId) => {
      if (!draggedTask) return;
      const visualIndex = getVisualDropIndex(e, toQuadrantId);
      const absoluteIndex = getAbsoluteDropIndex(toQuadrantId, visualIndex);

      moveTask(
        draggedTask.quadrantId,
        draggedTask.taskId,
        toQuadrantId,
        absoluteIndex
      );
      setDragOverQuadrantId(null);
      setDropPosition(null);
    },
    [draggedTask, moveTask, getVisualDropIndex, getAbsoluteDropIndex]
  );

  const hasCustomBg = Boolean(activeWallpaperUrl);
  const containerBgStyle = hasCustomBg
    ? { backgroundImage: `url("${activeWallpaperUrl}")` }
    : undefined;

  return (
    <div
      className={`container ${isDarkMode ? 'dark-mode' : ''} ${hasCustomBg ? 'has-custom-bg' : ''}`}
      style={containerBgStyle}
    >
      {hasCustomBg && (
        <div
          className="app-bg-overlay"
          style={{
            backdropFilter: `blur(${settings.backgroundBlur}px)`,
            WebkitBackdropFilter: `blur(${settings.backgroundBlur}px)`,
            backgroundColor: isDarkMode
              ? `rgba(15, 23, 42, ${settings.backgroundOverlayOpacity})`
              : `rgba(244, 247, 251, ${settings.backgroundOverlayOpacity})`,
          }}
        />
      )}
      {/* Hidden file input for Safari fallback */}
      <input
        ref={filePickerInput}
        type="file"
        accept=".json,application/json"
        hidden
        onChange={async (e) => {
          const result = await handleFileInputChange(e);
          if (result?.success) {
            const data = result.data;
            setQuadrants((prev) => {
              return prev.map((q) => {
                const importedQ = data.find((iq) => iq.id === q.id);
                if (importedQ) {
                  const validTasks = (importedQ.tasks || []).map(t => {
                    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(t.id));
                    return { ...t, id: isUuid ? t.id : crypto.randomUUID() };
                  });
                  return { ...importedQ, tasks: validTasks, dbId: q.dbId };
                }
                return q;
              });
            });
            showToast('File loaded successfully');
          }
        }}
      />

      {/* Header with theme toggle and settings button */}
      <Header
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        onOpenSettings={() => setIsSettingsOpen(true)}
      />

      {/* Gamification Level & Stats Bar */}
      <GamificationBar
        levelInfo={levelInfo}
        rankInfo={rankInfo}
        gameState={gameState}
        totalCompletedToday={totalCompletedToday}
        todayPlannedCount={stats.todayPlannedCount}
        viewMode={viewMode}
        onToggleViewMode={() => setViewMode((prev) => (prev === 'matrix' ? 'daily' : 'matrix'))}
        onOpenAnalytics={() => setIsAnalyticsOpen(true)}
      />

      {viewMode === 'daily' ? (
        <DailyFocusView
          quadrants={quadrants}
          onToggleTask={handleToggleTask}
          onTogglePlanToday={togglePlanToday}
          onBackToMatrix={() => setViewMode('matrix')}
          todayStr={todayStr}
        />
      ) : (
        <>
          {/* Mobile Tab Navigation */}
          <div className="mobile-quadrant-tabs">
            {quadrants.map((q) => {
              const activeCount = (q.tasks || []).filter((t) => !t.completed).length;
              return (
                <button
                  key={q.id}
                  className={`mobile-tab-btn ${activeMobileTab === q.id ? 'active' : ''} ${q.color}`}
                  onClick={() => setActiveMobileTab(q.id)}
                >
                  <span className="tab-dot"></span>
                  <span className="tab-title">{q.title}</span>
                  {activeCount > 0 && <span className="tab-count">{activeCount}</span>}
                </button>
              );
            })}
            <button
              className={`mobile-tab-btn all-tab ${activeMobileTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveMobileTab('all')}
            >
              <span className="tab-title">All</span>
              <span className="tab-count">
                {quadrants.reduce((acc, q) => acc + (q.tasks || []).filter((t) => !t.completed).length, 0)}
              </span>
            </button>
          </div>

          {/* Main quadrant grid */}
          <div className={`matrix-grid ${activeMobileTab !== 'all' ? 'single-tab-active' : ''}`}>
            {quadrants.map((q) => {
              const isVisible = activeMobileTab === 'all' || activeMobileTab === q.id;
              return (
                <Quadrant
                  key={q.id}
                  className={!isVisible ? 'mobile-hidden' : ''}
                  quadrant={q}
                  openConfigId={openConfigId}
                  dragOverQuadrantId={dragOverQuadrantId}
                  dropPosition={dropPosition}
                  draggedTask={draggedTask}
                  editingTask={editingTask}
                  inputs={inputs}
                  setInputs={setInputs}
                  taskListRefs={taskListRefs}
                  onToggleConfig={(id) => setOpenConfigId(openConfigId === id ? null : id)}
                  onUpdateQuadrant={updateQuadrant}
                  onToggleTask={handleToggleTask}
                  onRemoveTask={removeTask}
                  onTogglePlanToday={togglePlanToday}
                  onAddTask={(quadrantId) => {
                    addTask(quadrantId, inputs[quadrantId]);
                    setInputs((prev) => ({ ...prev, [quadrantId]: '' }));
                    setLastAddedQuadrantId(quadrantId);
                  }}
                  onDragStart={(quadrantId, taskId) =>
                    setDraggedTask({ quadrantId, taskId })
                  }
                  onDragEnd={() => setDraggedTask(null)}
                  onDragOver={(e) => handleDragOver(e, q.id)}
                  onDragEnter={() => setDragOverQuadrantId(q.id)}
                  onDragLeave={() => setDragOverQuadrantId(null)}
                  onDrop={(e) => handleDrop(e, q.id)}
                  onEditStart={setEditingTask}
                  onEditChange={(taskId, value) =>
                    setInputs((prev) => ({ ...prev, [`edit-task-${taskId}`]: value }))
                  }
                  onEditSave={saveTaskEdit}
                />
              );
            })}
          </div>
        </>
      )}

      {/* Toast notification */}
      <SaveToast show={showSaveToast} message={toastMessage} />

      {/* Gamification Reward Toast / Level Up Celebration */}
      <RewardToast reward={recentReward} onClose={clearRecentReward} />

      {/* Analytics Modal */}
      <AnalyticsModal
        isOpen={isAnalyticsOpen}
        onClose={() => setIsAnalyticsOpen(false)}
        stats={stats}
        gameState={gameState}
      />

      {/* App Settings Modal */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={settings}
        selectWallpaper={selectWallpaper}
        setCustomWallpaper={setCustomWallpaper}
        updateSetting={updateSetting}
        resetSettings={resetSettings}
        quadrants={quadrants}
        onUpdateQuadrant={updateQuadrant}
        user={user}
        fileHandle={fileHandle}
        isFileSystemSupported={isFileSystemSupported}
        isLegacyFileInputSupported={isLegacyFileInputSupported}
        loadData={loadData}
        loadDataLegacy={loadDataLegacy}
        saveData={saveData}
        loginWithGoogle={loginWithGoogle}
        logout={logout}
      />

      {/* Analytics integration */}
      <Analytics />
    </div>
  );
}

export default App;
